import { Outlet, createRootRoute, HeadContent, Scripts, Link, useLocation } from "@tanstack/react-router";
import { useEffect, useState, lazy, Suspense } from "react";

import appCss from "../styles.css?url";
import { SiteHeader } from "../components/SiteHeader";
import { SiteFooter } from "../components/SiteFooter";
import { CartDrawer } from "../components/CartDrawer";
import { Toaster } from "../components/ui/sonner";
import { useCartSync } from "../hooks/useCartSync";
import { LocaleContext, localeFromPath } from "@/lib/i18n";
import { trackPageView, bootstrapAnalytics } from "@/lib/analytics";
import { organizationJsonLd, localBusinessJsonLd } from "@/lib/seo";

// Defer non-critical, client-only UI (modals, banners, tour) so they don't
// bloat the initial JS bundle or block TTI. They render only after hydration
// and idle, preserving identical UX once the page is interactive.
const SiteTour = lazy(() =>
  import("../components/welcome/SiteTour").then((m) => ({ default: m.SiteTour })),
);
const CookieBanner = lazy(() =>
  import("../components/CookieBanner").then((m) => ({ default: m.CookieBanner })),
);
const ChatBubble = lazy(() =>
  import("../components/ChatBubble").then((m) => ({ default: m.ChatBubble })),
);
const SalePopup = lazy(() =>
  import("../components/SalePopup").then((m) => ({ default: m.SalePopup })),
);
const FloatingHelpPill = lazy(() =>
  import("../components/FloatingHelpPill").then((m) => ({ default: m.FloatingHelpPill })),
);



function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <p className="eyebrow">Lost in the showroom</p>
        <h1 className="mt-4 font-display text-7xl text-foreground">404</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          This shelf is empty. The page you were looking for was sold, moved, or never existed.
        </p>
        <div className="mt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-xs font-medium uppercase tracking-[0.2em] text-background hover:bg-terracotta"
          >
            Return home <span>→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { name: "google-site-verification", content: "k_6Xi4P8zgHPV7AaBF6nWSk9WMEEP3XRqJeP9yWHbog" },
      { name: "google-site-verification", content: "SK4cVH_kwh8eMnjWF1HrbdxD3c9TnTTngYYyKOXC1SI" },
      { name: "google-site-verification", content: "qMgrqVKJNPOtfKxEIXgQrqPzRLTXYdUle0F_DxdFQkQ" },
      { title: "Farveladeland — Vintage Murano Glass & Curated Flea Finds" },
      {
        name: "description",
        content:
          "A Copenhagen-based home for vintage Murano glass lamps, chandeliers and curated flea-market finds. Worldwide shipping, by-appointment showroom.",
      },
      { name: "author", content: "Farveladeland" },
      { property: "og:title", content: "Farveladeland — Vintage Murano Glass" },
      {
        property: "og:description",
        content:
          "Hand-blown Murano lamps, chandeliers and rare vintage finds, curated in Copenhagen.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      // Preload critical font files to shorten the CSS → font request chain
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "https://fonts.gstatic.com/s/fraunces/v38/6NU78FyLNQOQZAnv9bYEvDiIdE9Ea92uemAk_WBq8U_9v0c2Wa0KxC9TeP2Xz5c.woff2",
        crossOrigin: "anonymous",
      },
      {
        rel: "preload",
        as: "font",
        type: "font/woff2",
        href: "https://fonts.gstatic.com/s/inter/v20/UcC73FwrK3iLTeHuS_nVMrMxCp50SjIa1ZL7W0Q5nw.woff2",
        crossOrigin: "anonymous",
      },
      // Load Google Fonts CSS non-blocking via media-swap trick
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,400;1,9..144,400&family=Inter:wght@400;500&display=swap",
        media: "print",
        onLoad: "this.media='all'",
      } as any,
    ],
    scripts: [
      {
        children:
          "(function(){var l=document.querySelector('link[rel=\"stylesheet\"][media=\"print\"]');if(l){if(l.sheet){l.media='all';}else{l.addEventListener('load',function(){l.media='all';});}}})();",
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(organizationJsonLd()),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(localBusinessJsonLd()),
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  // SSR default is Danish (site's default locale). On the client we correct
  // <html lang> to "en" if the URL is under /en/* — runs before hydration.
  const langFixScript = `(function(){try{var p=location.pathname;if(p==="/en"||p.indexOf("/en/")===0){document.documentElement.lang="en";}}catch(e){}})();`;
  return (
    <html lang="da" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        <script dangerouslySetInnerHTML={{ __html: langFixScript }} />
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  useCartSync();
  const { pathname } = useLocation();
  const locale = localeFromPath(pathname);

  // Defer mounting non-critical overlays until the browser is idle so they
  // don't block hydration / TTI. UX is preserved — they all open on user
  // intent or timers, which fire well after idle anyway.
  const [deferredReady, setDeferredReady] = useState(false);
  useEffect(() => {
    const w = window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(() => setDeferredReady(true), { timeout: 2500 });
    } else {
      const id = window.setTimeout(() => setDeferredReady(true), 1500);
      return () => window.clearTimeout(id);
    }
  }, []);

  // Load GA if the visitor has already consented to statistics, and listen
  // for consent given later. Nothing is sent to Google before that.
  useEffect(() => {
    bootstrapAnalytics();
  }, []);

  // Fire a page_viewed event on every client-side route change. The first
  // server-rendered view fires on mount; subsequent SPA navigations fire when
  // pathname changes. Title is read after a microtask so the new route's
  // <head> has had a chance to apply.
  useEffect(() => {
    const id = window.setTimeout(() => {
      trackPageView({ locale });
    }, 0);
    return () => window.clearTimeout(id);
  }, [pathname, locale]);

  return (
    <LocaleContext.Provider value={locale}>
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <main>
          <Outlet />
        </main>
        <SiteFooter />
        <CartDrawer />
        <Toaster position="bottom-center" />
        {deferredReady && (
          <Suspense fallback={null}>
            <SiteTour />
            <CookieBanner />
            <ChatBubble />
            <SalePopup />
            <FloatingHelpPill />
          </Suspense>
        )}


      </div>
    </LocaleContext.Provider>
  );
}
